import React from 'react';
import { AlertCircle, CheckCircle2, Info, XCircle } from 'lucide-react';
import { cn } from '@/lib/utils';
import Icon, { IconComponent } from './Icon';

type Tone = 'info' | 'warning' | 'danger' | 'success';

interface AlertProps {
  tone?: Tone;
  title?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
}

const toneStyles: Record<Tone, { bg: string; fg: string; icon: IconComponent }> = {
  info: { bg: 'tw-bg-ds-info-bg', fg: 'tw-text-ds-info-fg', icon: Info },
  warning: { bg: 'tw-bg-ds-warning-bg', fg: 'tw-text-ds-warning-fg', icon: AlertCircle },
  danger: { bg: 'tw-bg-ds-danger-bg', fg: 'tw-text-ds-danger-fg', icon: XCircle },
  success: { bg: 'tw-bg-ds-success-bg', fg: 'tw-text-ds-success-fg', icon: CheckCircle2 },
};

/** Inline message box with a tone icon, optional title and body text. */
const Alert: React.FC<AlertProps> = ({ tone = 'info', title, children, className }) => {
  const { bg, fg, icon } = toneStyles[tone];
  return (
    <div
      role={tone === 'info' || tone === 'success' ? 'status' : 'alert'}
      className={cn('tw-flex tw-w-full tw-items-start tw-gap-3 tw-rounded-xl tw-p-[14px]', bg, className)}
    >
      <Icon icon={icon} size={20} className={fg} />
      <div className="tw-flex tw-min-w-0 tw-flex-1 tw-flex-col tw-gap-0.5">
        {title && <p className="tw-text-[14px] tw-font-semibold tw-leading-5 tw-text-ds-text-primary">{title}</p>}
        <p className="tw-text-[13px] tw-leading-[18px] tw-text-ds-text-secondary">{children}</p>
      </div>
    </div>
  );
};

export default Alert;
